/**
 * The current planned route, shared between the navigation UI and the scene.
 *
 * A plain channel rather than an event, for two reasons that both bit:
 *
 *   * **Ordering.** React runs child effects before parent ones, so the panel
 *     was publishing its route before the scene had subscribed — the very first
 *     route, including one restored from a saved target on page load, was
 *     dropped. Subscribing here delivers the current value immediately, so
 *     whoever arrives second still gets it.
 *   * **No import cycle.** The panel previously imported the event name from
 *     the scene, which imports the panel.
 */

import type { RoutePlan } from '@interspacial/shared';

type Listener = (plan: RoutePlan | null) => void;

let current: RoutePlan | null = null;
const listeners = new Set<Listener>();

export function publishRoute(plan: RoutePlan | null): void {
  current = plan;
  for (const listener of listeners) listener(plan);
}

/**
 * The route as it stands, for a caller that needs it once rather than onwards.
 *
 * `subscribeRoute` delivers the current value immediately, which covers anything
 * that wants to keep up. This is for the other case: a decision made once, while
 * something is being built, where subscribing would mean unsubscribing a line
 * later for a value that was already sitting there.
 */
export function currentRoute(): RoutePlan | null {
  return current;
}

/** Fires immediately with whatever the route is now, then on every change. */
export function subscribeRoute(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}
